
/* BBCrew — scroll reveal with extended cinematic hold */
(() => {
  'use strict';

  const sections = new Set();
  const videoStates = new WeakMap();
  const mobileProgress = new WeakMap();

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );
  const mobile = window.matchMedia('(max-width: 749px)');

  let queued = false;

  // Animation finishes at 65% of the scroll.
  // Remaining 35% holds the completed reveal.
  const REVEAL_FINISH = 0.65;

  const clamp = x => Math.max(0, Math.min(1, x));
  const ease = x => {
    x = clamp(x);
    return x * x * (3 - 2 * x);
  };

  function paintMobile(section, p) {
    const split = ease((p - 0.16) / 0.24);
    const reveal = ease((p - 0.22) / 0.40);
    const burst = Math.max(
      0,
      1 - Math.abs(p - 0.34) / 0.16
    );

    section.style.setProperty('--bb-progress', p.toFixed(4));
    section.style.setProperty('--bb-split', split.toFixed(4));
    section.style.setProperty('--bb-reveal', reveal.toFixed(4));
    section.style.setProperty('--bb-burst', burst.toFixed(4));

    section.classList.toggle('is-revealed', p >= 0.82);
  }

  function tapReveal(section) {
    if (!section || section.classList.contains('is-tap-revealed')) {
      return;
    }

    section.classList.add('is-tap-revealed');

    const button = section.querySelector('[data-bb-tap-reveal]');
    if (button) button.disabled = true;

    const video = section.querySelector('[data-bb-reveal-video]');

    if (video && !reducedMotion.matches) {
      video.currentTime = 0;
      video.play().catch(() => {
        // Use image fallback if playback fails.
      });
    }

    const start = performance.now();
    const duration = 1450;

    function frame(now) {
      if (!section.isConnected) return;

      const t = ease((now - start) / duration);

      mobileProgress.set(section, t);
      paintMobile(section, t);

      if (t < 1 && mobile.matches) {
        requestAnimationFrame(frame);
      }
    }

    requestAnimationFrame(frame);
  }

  function seekVideo(section, progress) {
    const state = videoStates.get(section);

    if (
      !state ||
      !state.ready ||
      reducedMotion.matches ||
      mobile.matches
    ) return;

    // Video completes before the final scroll hold.
    const position = ease((progress - 0.22) / 0.65);

    const desiredTime = Math.max(
      0,
      Math.min(
        state.duration - 0.04,
        position * state.duration
      )
    );

    if (
      Math.abs(state.video.currentTime - desiredTime) < 0.035
    ) return;

    state.target = desiredTime;

    if (state.video.seeking) return;

    try {
      state.video.currentTime = state.target;
    } catch (e) {
      // Image fallback remains visible.
    }
  }

  function setupVideo(section) {
    const video = section.querySelector('[data-bb-reveal-video]');

    if (!video || videoStates.has(section)) return;

    const state = {
      video,
      ready: false,
      duration: 0,
      target: 0
    };

    videoStates.set(section, state);

    const ready = () => {
      if (
        !Number.isFinite(video.duration) ||
        video.duration <= 0 ||
        !video.seekable.length
      ) return;

      state.duration = video.duration;
      state.ready = true;

      if (!mobile.matches) video.pause();

      section.classList.add('has-video-ready');
      schedule();
    };

    video.addEventListener('loadedmetadata', ready);
    video.addEventListener('canplay', ready);

    video.addEventListener('seeked', () => {
      if (!state.ready || mobile.matches) return;

      if (
        Math.abs(video.currentTime - state.target) > 0.035
      ) {
        try {
          video.currentTime = state.target;
        } catch (e) {}
      }
    });

    video.addEventListener('error', () => {
      section.classList.remove('has-video-ready');
    });

    if (video.readyState >= 1) ready();
  }

  function update() {
    queued = false;

    const viewport = window.innerHeight || 1;
    let bannerBehindHeader = false;

    for (const section of sections) {
      if (!section.isConnected) {
        sections.delete(section);
        continue;
      }

      const r = section.getBoundingClientRect();

      const header = document.querySelector(
        '.header-section .site-header'
      );

      const headerHeight = header
        ? header.getBoundingClientRect().height
        : 100;

      if (
        r.top < headerHeight &&
        r.bottom > headerHeight
      ) {
        bannerBehindHeader = true;
      }

      const travel = Math.max(1, r.height - viewport);

      // Mobile uses TAP TO REVEAL, not scroll scrubbing.
      if (mobile.matches && !reducedMotion.matches) {
        paintMobile(
          section,
          mobileProgress.get(section) || 0
        );
        continue;
      }

      // Actual page scroll progress: 0 to 1.
      const p = clamp(-r.top / travel);

      // Animation completes at 65%.
      const animationProgress = clamp(
        p / REVEAL_FINISH
      );

      const split = ease(
        (animationProgress - 0.16) / 0.24
      );

      const reveal = ease(
        (animationProgress - 0.22) / 0.40
      );

      const burst = Math.max(
        0,
        1 - Math.abs(animationProgress - 0.34) / 0.16
      );

      section.style.setProperty(
        '--bb-progress',
        animationProgress.toFixed(4)
      );

      section.style.setProperty(
        '--bb-split',
        split.toFixed(4)
      );

      section.style.setProperty(
        '--bb-reveal',
        reveal.toFixed(4)
      );

      section.style.setProperty(
        '--bb-burst',
        burst.toFixed(4)
      );

      section.classList.toggle(
        'is-revealed',
        animationProgress >= 0.82
      );

      // Video stops at final frame during the hold.
      seekVideo(section, animationProgress);
    }

    document.body.classList.toggle(
      'bb-reveal-over-header',
      bannerBehindHeader
    );
  }

  function schedule() {
    if (!queued) {
      queued = true;
      requestAnimationFrame(update);
    }
  }

  function initialise(root = document) {
    if (root.matches?.('[data-bb-scroll-reveal]')) {
      sections.add(root);
      setupVideo(root);
    }

    root.querySelectorAll?.('[data-bb-scroll-reveal]')
      .forEach(section => {
        sections.add(section);
        setupVideo(section);
      });

    root.querySelectorAll?.('[data-bb-tap-reveal]')
      .forEach(button => {
        if (button.dataset.tapBound) return;

        button.dataset.tapBound = '1';

        button.addEventListener('click', () => {
          tapReveal(
            button.closest('[data-bb-scroll-reveal]')
          );
        });
      });

    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialise(),
      { once: true }
    );
  } else {
    initialise();
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });

  document.addEventListener('shopify:section:load', e => {
    initialise(e.target);
  });

  document.addEventListener('shopify:section:unload', e => {
    if (e.target.matches?.('[data-bb-scroll-reveal]')) {
      sections.delete(e.target);
    }

    e.target.querySelectorAll?.('[data-bb-scroll-reveal]')
      .forEach(section => sections.delete(section));

    schedule();
  });
})();
